import { trackEvent } from '@vp/om-newrelic-tracker';
import {
  createTrackingErrorData,
  createTrackingEventData,
  sanitizeError,
} from './newRelicTrackingUtils';

// Cancellation custom events
const CANCELLATION_ATTEMPTED = 'orders-ui-cancellation-attempted';
const CANCELLATION_REQUEST_FAILED = 'orders-ui-cancellation-request-failed';
const CANCELLATION_REQUEST_SUCCESS = 'orders-ui-cancellation-request-success';

// Shipping address change request events
const SHIPPING_ADDRESS_CHANGE_ATTEMPTED =
  'orders-ui-shippingaddr-change-attempted';
const SHIPPING_ADDRESS_CHANGE_REQUEST_FAILED =
  'orders-ui-shippingaddr-change-request-failed';
const SHIPPING_ADDRESS_CHANGE_REQUEST_SUCCESS =
  'orders-ui-shippingaddr-change-request-success';

// Customer feedback request events
const CUSTOMER_FEEDBACK_SUBTYPE = 'orders-ui-customer-feedback-request';
const CUSTOMER_FEEDBACK_SUBMISSION_ATTEMPTED = `${CUSTOMER_FEEDBACK_SUBTYPE}-submission-attempted`;
const CUSTOMER_FEEDBACK_SUBMISSION_SUCCESS = `${CUSTOMER_FEEDBACK_SUBTYPE}-submission-success`;
const CUSTOMER_FEEDBACK_SUBMISSION_FAILED = `${CUSTOMER_FEEDBACK_SUBTYPE}-submission-failed`;

/**
 * A shopper attempted to submit customer feedback
 * Inspect 'additionalData' for 'feedbackType' to filter for a specific type of request (i.e. 'DOCUMENT_REVIEW')
 */
export const trackCustomerFeedbackSubmissionAttempted = (
  shopperId: string,
  additionalData: object
) => {
  const eventDate = createTrackingEventData(
    CUSTOMER_FEEDBACK_SUBMISSION_ATTEMPTED,
    shopperId,
    additionalData
  );
  trackEvent(eventDate);
};

/**
 * A shopper successfully submitted customer feedback
 * Inspect 'additionalData' for 'feedbackType' to filter for a specific type of request (i.e. 'DOCUMENT_REVIEW')
 */
export const trackCustomerFeedbackSubmissionSuccess = (
  shopperId: string,
  additionalData: object
) => {
  const eventDate = createTrackingEventData(
    CUSTOMER_FEEDBACK_SUBMISSION_SUCCESS,
    shopperId,
    additionalData
  );
  trackEvent(eventDate);
};

/**
 * An error occurred while submitting customer feedback
 * Inspect 'additionalData' for 'feedbackType' to filter for a specific type of request (i.e. 'DOCUMENT_REVIEW')
 */
export const trackCustomerFeedbackSubmissionFailed = (
  shopperId: string,
  err: object,
  additionalData: object
) => {
  const error = sanitizeError(err);
  const errorData = createTrackingErrorData(
    CUSTOMER_FEEDBACK_SUBMISSION_FAILED,
    error,
    shopperId,
    additionalData
  );
  trackEvent(errorData);
};

/**
 * A shopper attempted to submit customer feedback
 * Inspect 'additionalData' for 'feedbackType' to filter for a specific type of request (i.e. 'DOCUMENT_REVIEW')
 */
export const trackAnonymousCustomerFeedbackSubmissionAttempted = (
  additionalData: object
) => {
  const eventDate = createTrackingEventData(
    CUSTOMER_FEEDBACK_SUBMISSION_ATTEMPTED,
    '',
    additionalData
  );
  trackEvent(eventDate);
};

/**
 * A shopper successfully submitted customer feedback
 * Inspect 'additionalData' for 'feedbackType' to filter for a specific type of request (i.e. 'DOCUMENT_REVIEW')
 */
export const trackAnonymousCustomerFeedbackSubmissionSuccess = (
  additionalData: object
) => {
  const eventDate = createTrackingEventData(
    CUSTOMER_FEEDBACK_SUBMISSION_SUCCESS,
    '',
    additionalData
  );
  trackEvent(eventDate);
};

/**
 * An error occurred while submitting customer feedback
 * Inspect 'additionalData' for 'feedbackType' to filter for a specific type of request (i.e. 'DOCUMENT_REVIEW')
 */
export const trackAnonymousCustomerFeedbackSubmissionFailed = (
  err: object,
  additionalData: object
) => {
  const error = sanitizeError(err);
  const errorData = createTrackingErrorData(
    CUSTOMER_FEEDBACK_SUBMISSION_FAILED,
    error,
    '',
    additionalData
  );
  trackEvent(errorData);
};

/**
 * A customer attempted to cancel a set of item(s)
 */
export const trackCancellationAttempted = (
  shopperId: string,
  additionalData: object
) => {
  const eventDate = createTrackingEventData(
    CANCELLATION_ATTEMPTED,
    shopperId,
    additionalData
  );
  trackEvent(eventDate);
};

/**
 * A customer successfully requested cancellation of item(s)
 */
export const trackCancelRequestSuccess = (
  shopperId: string,
  additionalData: object
) => {
  const eventDate = createTrackingEventData(
    CANCELLATION_REQUEST_SUCCESS,
    shopperId,
    additionalData
  );
  trackEvent(eventDate);
};

/**
 * A customer failed to reorder a set of item(s)
 */
export const trackCancelRequestFailed = (
  shopperId: string,
  err: object,
  additionalData: object
) => {
  const error = sanitizeError(err);
  const errorData = createTrackingErrorData(
    CANCELLATION_REQUEST_FAILED,
    error,
    shopperId,
    additionalData
  );
  trackEvent(errorData);
};

/**
 * A customer attempted to request a shipping address change
 */
export const trackShippingAddressChangeAttempted = (
  shopperId: string,
  additionalData: object
) => {
  const eventDate = createTrackingEventData(
    SHIPPING_ADDRESS_CHANGE_ATTEMPTED,
    shopperId,
    additionalData
  );
  trackEvent(eventDate);
};

/**
 * A customer successfully requested a shipping address change
 */
export const trackShippingAddressChangeRequestSuccess = (
  shopperId: string,
  additionalData: object
) => {
  const eventDate = createTrackingEventData(
    SHIPPING_ADDRESS_CHANGE_REQUEST_SUCCESS,
    shopperId,
    additionalData
  );
  trackEvent(eventDate);
};

/**
 * A customer failed to request a shipping address change
 */
export const trackShippingAddressChangeRequestFailed = (
  shopperId: string,
  err: object,
  additionalData: object
) => {
  const error = sanitizeError(err);
  const errorData = createTrackingErrorData(
    SHIPPING_ADDRESS_CHANGE_REQUEST_FAILED,
    error,
    shopperId,
    additionalData
  );
  trackEvent(errorData);
};
